// Tiny, dependency-free frontmatter parser for our content files. Runtime-safe (no Node
// APIs, no eval), so it works in the browser, SSR and Cloudflare Workers. Supports exactly
// what our articles use:   key: "string" | key: 'string' | key: bare | key: true/false | key: ["a", "b"]

export interface ParsedDoc {
  data: Record<string, string | boolean | string[]>;
  content: string;
}

function parseScalar(raw: string): string | boolean {
  const v = raw.trim();
  if (v === "true") return true;
  if (v === "false") return false;
  if (
    (v.startsWith('"') && v.endsWith('"')) ||
    (v.startsWith("'") && v.endsWith("'"))
  ) {
    const inner = v.slice(1, -1);
    return v.startsWith('"')
      ? inner.replace(/\\"/g, '"').replace(/\\\\/g, "\\")
      : inner.replace(/''/g, "'");
  }
  return v;
}

function parseArray(raw: string): string[] {
  const inner = raw.trim().slice(1, -1);
  const out: string[] = [];
  let cur = "";
  let quote: string | null = null;
  for (const ch of inner) {
    if (quote) {
      if (ch === quote) quote = null;
      else cur += ch;
    } else if (ch === '"' || ch === "'") quote = ch;
    else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  if (cur.trim() || out.length) out.push(cur.trim());
  return out.filter((s) => s.length > 0);
}

export function parseFrontmatter(source: string): ParsedDoc {
  const text = (
    source.charCodeAt(0) === 0xfeff ? source.slice(1) : source
  ).replace(/\r\n/g, "\n");
  const m = text.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!m) return { data: {}, content: text };
  const data: ParsedDoc["data"] = {};
  for (const line of (m[1] ?? "").split("\n")) {
    if (!line.trim() || line.trim().startsWith("#")) continue;
    const idx = line.indexOf(":");
    if (idx < 1) continue;
    const key = line.slice(0, idx).trim();
    const val = line.slice(idx + 1).trim();
    data[key] =
      val.startsWith("[") && val.endsWith("]")
        ? parseArray(val)
        : parseScalar(val);
  }
  return { data, content: m[2] ?? "" };
}

/** Words and whole-minute reading time at 200 wpm (min 1). */
export function readingStats(body: string): { words: number; minutes: number } {
  const words = body.trim().split(/\s+/).filter(Boolean).length;
  return { words, minutes: Math.max(1, Math.ceil(words / 200)) };
}
